export interface Lead {
  id: number;
  name: string;
  company: string;
  phone: string;
  email: string;
  source: LeadSource;
  sourceAccount?: string;
  stage: LeadStage;
  priority: Priority;
  assigned: string;
  industry: Industry;
  lastActivity: string;
  value: string;
  score: number;
  scoreFactors: ScoreFactor[];
  customFields?: Record<string, any>;
  extraFields?: Record<string, any>;
  projectId?: string | null;
  project_id?: string | null;
  projectName?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface ScoreFactor {
  label: string;
  score: number;
  max: number;
}

export type LeadSource = "IndiaMart" | "JustDial" | "TradeIndia" | "WhatsApp" | "Website" | "Social Media" | "Manual";
export type LeadStage = "New Inquiry" | "Site Visit Scheduled" | "Site Visit Done" | "Proposal Sent" | "Negotiation" | "Booking Done" | "Lost";
export type Priority = "High" | "Medium" | "Low";
export type Industry = "Real Estate" | "Manufacturing" | "Education" | "Healthcare" | "Retail" | "SaaS" | "Construction";

export const sourceBadgeColors: Record<LeadSource, string> = {
  IndiaMart: "bg-orange-50 text-orange-700",
  JustDial: "bg-blue-50 text-blue-700",
  TradeIndia: "bg-teal-50 text-teal-700",
  WhatsApp: "bg-green-50 text-green-700",
  Website: "bg-purple-50 text-purple-700",
  "Social Media": "bg-pink-50 text-pink-700",
  Manual: "bg-gray-100 text-gray-600",
};

export const stageBadgeColors: Record<LeadStage, string> = {
  "New Inquiry": "bg-gray-100 text-gray-700",
  "Site Visit Scheduled": "bg-blue-50 text-blue-700",
  "Site Visit Done": "bg-indigo-50 text-indigo-700",
  "Proposal Sent": "bg-amber-50 text-amber-700",
  "Negotiation": "bg-orange-50 text-orange-700",
  "Booking Done": "bg-green-50 text-green-700",
  "Lost": "bg-red-50 text-red-600",
};

export const priorityBadgeColors: Record<Priority, string> = {
  High: "bg-red-50 text-red-600",
  Medium: "bg-amber-50 text-amber-600",
  Low: "bg-green-50 text-green-600",
};

export const priorityDotColors: Record<Priority, string> = {
  High: "bg-red-500",
  Medium: "bg-amber-500",
  Low: "bg-green-500",
};

export const allSources: LeadSource[] = ["IndiaMart", "JustDial", "TradeIndia", "WhatsApp", "Website", "Social Media", "Manual"];
export const allStages: LeadStage[] = ["New Inquiry", "Site Visit Scheduled", "Site Visit Done", "Proposal Sent", "Negotiation", "Booking Done", "Lost"];
export const allPriorities: Priority[] = ["High", "Medium", "Low"];
export const allIndustries: Industry[] = ["Real Estate", "Manufacturing", "Education", "Healthcare", "Retail", "SaaS", "Construction"];
export const allAgents: string[] = ["Rajesh Kumar", "Priya Sharma", "Amit Patel", "Sneha Rao"];

export type ScoreCategory = "all" | "hot" | "warm" | "cold";

export function getScoreCategory(score: number): "hot" | "warm" | "cold" {
  if (score >= 71) return "hot";
  if (score >= 41) return "warm";
  return "cold";
}

export function getScoreBadgeColors(score: number): string {
  if (score >= 71) return "bg-green-100 text-green-700";
  if (score >= 41) return "bg-amber-100 text-amber-700";
  return "bg-red-100 text-red-700";
}

export function getScoreLabel(score: number): string {
  if (score >= 71) return "Hot";
  if (score >= 41) return "Warm";
  return "Cold";
}

export const leadsData: Lead[] = [
  {
    id: 1, name: "Vikram Mehta", company: "TechVentures Inc",
    phone: "9876543210", email: "vikram@techventures.com",
    source: "IndiaMart", sourceAccount: "Mumbai", stage: "Negotiation", priority: "High",
    assigned: "Rajesh Kumar", industry: "Real Estate",
    lastActivity: "2 hours ago", value: "₹24,500",
    score: 87,
    scoreFactors: [
      { label: "Source quality (IndiaMart)", score: 20, max: 20 },
      { label: "Response time (<1hr)", score: 18, max: 20 },
      { label: "Stage (Negotiation)", score: 20, max: 20 },
      { label: "Engagement level", score: 15, max: 20 },
      { label: "Deal value (₹24.5K)", score: 14, max: 20 },
    ],
  },
  {
    id: 2, name: "Anita Desai", company: "GreenLeaf Organics",
    phone: "9845123456", email: "anita@greenleaf.com",
    source: "JustDial", stage: "Proposal Sent", priority: "Medium",
    assigned: "Priya Sharma", industry: "Real Estate",
    lastActivity: "5 hours ago", value: "₹12,800",
    score: 65,
    scoreFactors: [
      { label: "Source quality (JustDial)", score: 14, max: 20 },
      { label: "Response time (<2hr)", score: 12, max: 20 },
      { label: "Stage (Proposal Sent)", score: 16, max: 20 },
      { label: "Engagement level", score: 13, max: 20 },
      { label: "Deal value (₹12.8K)", score: 10, max: 20 },
    ],
  },
  {
    id: 3, name: "Suresh Nair", company: "Skyline Architects",
    phone: "9712345678", email: "suresh@skyline.com",
    source: "Website", stage: "Site Visit Done", priority: "High",
    assigned: "Rajesh Kumar", industry: "Real Estate",
    lastActivity: "1 day ago", value: "₹45,200",
    score: 78,
    scoreFactors: [
      { label: "Source quality (Website)", score: 16, max: 20 },
      { label: "Response time (<1hr)", score: 17, max: 20 },
      { label: "Stage (Site Visit Done)", score: 17, max: 20 },
      { label: "Engagement level", score: 14, max: 20 },
      { label: "Deal value (₹45.2K)", score: 14, max: 20 },
    ],
  },
  {
    id: 4, name: "Kavita Joshi", company: "BluePeak Media",
    phone: "9632145870", email: "kavita@bluepeak.com",
    source: "Social Media", stage: "Booking Done", priority: "Low",
    assigned: "Amit Patel", industry: "Real Estate",
    lastActivity: "2 days ago", value: "₹8,900",
    score: 92,
    scoreFactors: [
      { label: "Source quality (Social Media)", score: 18, max: 20 },
      { label: "Response time (<30min)", score: 20, max: 20 },
      { label: "Stage (Booking Done)", score: 20, max: 20 },
      { label: "Engagement level", score: 17, max: 20 },
      { label: "Deal value (₹8.9K)", score: 17, max: 20 },
    ],
  },
  {
    id: 5, name: "Rahul Verma", company: "NovaTech Labs",
    phone: "9521478963", email: "rahul@novatech.com",
    source: "WhatsApp", stage: "New Inquiry", priority: "Medium",
    assigned: "Sneha Rao", industry: "Real Estate",
    lastActivity: "30 mins ago", value: "₹31,000",
    score: 43,
    scoreFactors: [
      { label: "Source quality (WhatsApp)", score: 10, max: 20 },
      { label: "Response time (<3hr)", score: 8, max: 20 },
      { label: "Stage (New Inquiry)", score: 8, max: 20 },
      { label: "Engagement level", score: 10, max: 20 },
      { label: "Deal value (₹31K)", score: 7, max: 20 },
    ],
  },
  {
    id: 6, name: "Deepika Singh", company: "EduSmart",
    phone: "9415263780", email: "deepika@edusmart.com",
    source: "IndiaMart", sourceAccount: "Pune", stage: "Site Visit Scheduled", priority: "High",
    assigned: "Priya Sharma", industry: "Real Estate",
    lastActivity: "3 hours ago", value: "₹18,500",
    score: 71,
    scoreFactors: [
      { label: "Source quality (IndiaMart)", score: 20, max: 20 },
      { label: "Response time (<1hr)", score: 14, max: 20 },
      { label: "Stage (Site Visit Scheduled)", score: 15, max: 20 },
      { label: "Engagement level", score: 12, max: 20 },
      { label: "Deal value (₹18.5K)", score: 10, max: 20 },
    ],
  },
  {
    id: 7, name: "Arjun Kapoor", company: "MediCare Plus",
    phone: "9308547621", email: "arjun@medicare.com",
    source: "JustDial", sourceAccount: "Pune", stage: "Lost", priority: "Low",
    assigned: "Amit Patel", industry: "Real Estate",
    lastActivity: "5 days ago", value: "₹7,200",
    score: 28,
    scoreFactors: [
      { label: "Source quality (JustDial)", score: 8, max: 20 },
      { label: "Response time (>24hr)", score: 2, max: 20 },
      { label: "Stage (Lost)", score: 5, max: 20 },
      { label: "Engagement level", score: 8, max: 20 },
      { label: "Deal value (₹7.2K)", score: 5, max: 20 },
    ],
  },
  {
    id: 8, name: "Sunita Rao", company: "PropMax Realty",
    phone: "9201364875", email: "sunita@propmax.com",
    source: "JustDial", sourceAccount: "Mumbai", stage: "Site Visit Done", priority: "High",
    assigned: "Rajesh Kumar", industry: "Real Estate",
    lastActivity: "4 hours ago", value: "₹95,000",
    score: 84,
    scoreFactors: [
      { label: "Source quality (TradeIndia)", score: 17, max: 20 },
      { label: "Response time (<1hr)", score: 18, max: 20 },
      { label: "Stage (Site Visit Done)", score: 17, max: 20 },
      { label: "Engagement level", score: 16, max: 20 },
      { label: "Deal value (₹95K)", score: 16, max: 20 },
    ],
  },
  {
    id: 9, name: "Manoj Tiwari", company: "FastBuild Corp",
    phone: "9854123670", email: "manoj@fastbuild.com",
    source: "Website", stage: "Proposal Sent", priority: "Medium",
    assigned: "Priya Sharma", industry: "Real Estate",
    lastActivity: "1 hour ago", value: "₹52,000",
    score: 56,
    scoreFactors: [
      { label: "Source quality (Website)", score: 12, max: 20 },
      { label: "Response time (<2hr)", score: 11, max: 20 },
      { label: "Stage (Proposal Sent)", score: 13, max: 20 },
      { label: "Engagement level", score: 11, max: 20 },
      { label: "Deal value (₹52K)", score: 9, max: 20 },
    ],
  },
  {
    id: 10, name: "Pooja Menon", company: "StyleHub",
    phone: "9741258036", email: "pooja@stylehub.com",
    source: "Social Media", stage: "New Inquiry", priority: "Low",
    assigned: "Sneha Rao", industry: "Real Estate",
    lastActivity: "Just now", value: "₹5,500",
    score: 35,
    scoreFactors: [
      { label: "Source quality (Social Media)", score: 6, max: 20 },
      { label: "Response time (<4hr)", score: 5, max: 20 },
      { label: "Stage (New Inquiry)", score: 7, max: 20 },
      { label: "Engagement level", score: 10, max: 20 },
      { label: "Deal value (₹5.5K)", score: 7, max: 20 },
    ],
  },
  {
    id: 11, name: "Rohit Bansal", company: "CementPro Ltd",
    phone: "9123456789", email: "rohit@cementpro.com",
    source: "IndiaMart", stage: "Site Visit Done", priority: "High",
    assigned: "Rajesh Kumar", industry: "Real Estate",
    lastActivity: "6 hours ago", value: "₹68,000",
    score: 76,
    scoreFactors: [
      { label: "Source quality (IndiaMart)", score: 20, max: 20 },
      { label: "Response time (<1hr)", score: 13, max: 20 },
      { label: "Stage (Site Visit Done)", score: 17, max: 20 },
      { label: "Engagement level", score: 13, max: 20 },
      { label: "Deal value (₹68K)", score: 13, max: 20 },
    ],
  },
  {
    id: 12, name: "Lakshmi Iyer", company: "HealthFirst Clinics",
    phone: "9988776655", email: "lakshmi@healthfirst.com",
    source: "WhatsApp", stage: "Site Visit Scheduled", priority: "Medium",
    assigned: "Priya Sharma", industry: "Real Estate",
    lastActivity: "8 hours ago", value: "₹22,300",
    score: 58,
    scoreFactors: [
      { label: "Source quality (WhatsApp)", score: 12, max: 20 },
      { label: "Response time (<2hr)", score: 10, max: 20 },
      { label: "Stage (Site Visit Scheduled)", score: 13, max: 20 },
      { label: "Engagement level", score: 12, max: 20 },
      { label: "Deal value (₹22.3K)", score: 11, max: 20 },
    ],
  },
  {
    id: 13, name: "Gaurav Saxena", company: "EduPrime Academy",
    phone: "9871234560", email: "gaurav@eduprime.com",
    source: "TradeIndia", stage: "Proposal Sent", priority: "High",
    assigned: "Amit Patel", industry: "Real Estate",
    lastActivity: "12 hours ago", value: "₹41,000",
    score: 72,
    scoreFactors: [
      { label: "Source quality (TradeIndia)", score: 16, max: 20 },
      { label: "Response time (<1hr)", score: 15, max: 20 },
      { label: "Stage (Proposal Sent)", score: 14, max: 20 },
      { label: "Engagement level", score: 14, max: 20 },
      { label: "Deal value (₹41K)", score: 13, max: 20 },
    ],
  },
  {
    id: 14, name: "Meera Kulkarni", company: "UrbanNest Realtors",
    phone: "9732109876", email: "meera@urbannest.com",
    source: "Website", stage: "New Inquiry", priority: "Low",
    assigned: "Sneha Rao", industry: "Real Estate",
    lastActivity: "1 day ago", value: "₹78,500",
    score: 49,
    scoreFactors: [
      { label: "Source quality (Website)", score: 12, max: 20 },
      { label: "Response time (>4hr)", score: 6, max: 20 },
      { label: "Stage (New Inquiry)", score: 8, max: 20 },
      { label: "Engagement level", score: 12, max: 20 },
      { label: "Deal value (₹78.5K)", score: 11, max: 20 },
    ],
  },
  {
    id: 15, name: "Tushar Gokhale", company: "AutoParts Hub",
    phone: "9654321098", email: "tushar@autoparts.com",
    source: "Manual", stage: "Site Visit Scheduled", priority: "Medium",
    assigned: "Sneha Rao", industry: "Real Estate",
    lastActivity: "4 hours ago", value: "₹15,800",
    score: 47,
    scoreFactors: [
      { label: "Source quality (Manual)", score: 10, max: 20 },
      { label: "Response time (<3hr)", score: 8, max: 20 },
      { label: "Stage (Site Visit Scheduled)", score: 12, max: 20 },
      { label: "Engagement level", score: 9, max: 20 },
      { label: "Deal value (₹15.8K)", score: 8, max: 20 },
    ],
  },
  {
    id: 16, name: "Ravi Iyer", company: "CloudFirst Solutions",
    phone: "9812345670", email: "ravi@cloudfirst.com",
    source: "Website", stage: "Negotiation", priority: "High",
    assigned: "Priya Sharma", industry: "Real Estate",
    lastActivity: "1 hour ago", value: "₹68,000",
    score: 81,
    scoreFactors: [
      { label: "Source quality (Website)", score: 15, max: 20 },
      { label: "Response time (<1hr)", score: 17, max: 20 },
      { label: "Stage (Negotiation)", score: 19, max: 20 },
      { label: "Engagement level", score: 16, max: 20 },
      { label: "Deal value (₹68K)", score: 14, max: 20 },
    ],
  },
  {
    id: 17, name: "Priya Nambiar", company: "StyleCraft Interiors",
    phone: "9654109876", email: "priya@stylecraft.com",
    source: "IndiaMart", stage: "Booking Done", priority: "Medium",
    assigned: "Amit Patel", industry: "Real Estate",
    lastActivity: "3 days ago", value: "₹33,000",
    score: 88,
    scoreFactors: [
      { label: "Source quality (IndiaMart)", score: 20, max: 20 },
      { label: "Response time (<1hr)", score: 19, max: 20 },
      { label: "Stage (Booking Done)", score: 20, max: 20 },
      { label: "Engagement level", score: 15, max: 20 },
      { label: "Deal value (₹33K)", score: 14, max: 20 },
    ],
  },
];